import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  FormControlLabel,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import {
  addDays,
  addMonths,
  addWeeks,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isWithinInterval,
  max as dateMax,
  min as dateMin,
  startOfMonth,
  startOfToday,
  startOfWeek,
  subMonths,
  subWeeks
} from 'date-fns';
import type { Locale } from 'date-fns';
import { enUS, lt } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { alpha, useTheme } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';
import { ProjectTimelineTaskEvent } from '../utils/projectTimeline';
import { getTaskPriorityBarSx, projectTimelineRowBand } from '../utils/projectAndTaskColors';

export type TimelineRangeMode = 'week' | 'month' | 'full';

interface ProjectTimelineProps {
  tasks: ProjectTimelineTaskEvent[];
  /** Opens the same task detail dialog as elsewhere when a row is clicked */
  onTaskClick?: (projectId: string, taskId: string) => void;
}

/** Responsive label column — avoids a wide fixed 220px strip on every row */
const LABEL_COL_SX: SxProps<Theme> = {
  width: { xs: 128, sm: 152, md: 172 },
  flexShrink: 0,
  minWidth: 0,
  boxSizing: 'border-box'
};

const dayKey = (d: Date) => format(d, 'yyyy-MM-dd');

function taskRowMinPx(task: ProjectTimelineTaskEvent) {
  return task.projectTitle ? 44 : 32;
}

function taskTimelineTooltip(
  task: ProjectTimelineTaskEvent,
  start: Date,
  end: Date,
  dateLocale: Locale,
  t: TFunction
) {
  const statusLabel =
    task.timelineDone && task.status ? t(`tasks.status.${task.status}`) : '';

  return (
    <Box>
      {task.projectTitle && (
        <Typography variant="caption" display="block" color="grey.300" noWrap>
          {task.projectTitle}
        </Typography>
      )}
      <Typography variant="subtitle2" noWrap>
        {task.title}
      </Typography>
      <Typography variant="caption">
        {format(start, 'yyyy-MM-dd', { locale: dateLocale })} – {format(end, 'yyyy-MM-dd', { locale: dateLocale })}
      </Typography>
      {task.assigneeNames.length > 0 && (
        <>
          <br />
          <Typography variant="caption">{task.assigneeNames.join(', ')}</Typography>
        </>
      )}
      {task.timelineDone && (
        <>
          <br />
          <Typography variant="caption" color="success.light">
            {t('projects.timelineDoneHint', { status: statusLabel || '—' })}
          </Typography>
        </>
      )}
    </Box>
  );
}

const BAR_VERTICAL_INSET = 6;

/** Stable light background per calendar month (theme-aware). */
function timelineMonthBackground(theme: Theme, monthSeed: number, level: 'band' | 'day') {
  const opacity = level === 'band' ? 0.14 : 0.08;
  const colors = [
    theme.palette.primary.main,
    theme.palette.info.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.secondary?.main ?? theme.palette.primary.main,
    theme.palette.error.main
  ];
  const i = ((monthSeed % colors.length) + colors.length) % colors.length;
  return alpha(colors[i], opacity);
}

interface TimelineMonthSegment {
  year: number;
  monthIndex: number;
  count: number;
  label: string;
  labelFull: string;
}

function buildMonthSegments(days: Date[], dateLocale: Locale): TimelineMonthSegment[] {
  if (days.length === 0) return [];
  const segments: TimelineMonthSegment[] = [];
  let i = 0;
  while (i < days.length) {
    const d0 = days[i];
    const y = d0.getFullYear();
    const m = d0.getMonth();
    let j = i + 1;
    while (j < days.length && days[j].getFullYear() === y && days[j].getMonth() === m) {
      j++;
    }
    segments.push({
      year: y,
      monthIndex: m,
      count: j - i,
      label: format(d0, 'MMMM yyyy', { locale: dateLocale }),
      labelFull: format(d0, 'PPPP', { locale: dateLocale })
    });
    i = j;
  }
  return segments;
}

const ProjectTimeline: React.FC<ProjectTimelineProps> = ({ tasks, onTaskClick }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const dateLocale = i18n.language.startsWith('lt') ? lt : enUS;

  const [rangeMode, setRangeMode] = useState<TimelineRangeMode>('month');
  const [anchorDate, setAnchorDate] = useState(() => startOfToday());
  const [hideCompletedTasks, setHideCompletedTasks] = useState(false);

  const { windowStart, windowEnd } = useMemo(() => {
    if (rangeMode === 'full') {
      if (tasks.length === 0) {
        const today = startOfToday();
        return { windowStart: addDays(today, -45), windowEnd: addDays(today, 45) };
      }

      const dates: Date[] = [];
      for (const task of tasks) {
        dates.push(new Date(task.startDate), new Date(task.endDate));
      }
      let minD = dateMin(dates);
      let maxD = dateMax(dates);

      // Add breathing room so single-day bars are visible.
      minD = addDays(minD, -2);
      maxD = addDays(maxD, 2);
      return { windowStart: minD, windowEnd: maxD };
    }

    if (rangeMode === 'week') {
      const ws = startOfWeek(anchorDate, { weekStartsOn: 1 });
      const we = endOfWeek(anchorDate, { weekStartsOn: 1 });
      return { windowStart: ws, windowEnd: we };
    }

    // Month: exactly one calendar month (anchor). Tasks that extend outside are clipped in the bar.
    return { windowStart: startOfMonth(anchorDate), windowEnd: endOfMonth(anchorDate) };
  }, [anchorDate, rangeMode]);

  const days = useMemo(
    () =>
      eachDayOfInterval({
        start: windowStart,
        end: windowEnd
      }),
    [windowStart, windowEnd]
  );

  const dayKeys = useMemo(() => days.map((d) => dayKey(d)), [days]);
  const dayCount = Math.max(dayKeys.length, 1);

  const monthSegments = useMemo(
    () => buildMonthSegments(days, dateLocale),
    [days, dateLocale]
  );

  const visibleTasks = useMemo(
    () =>
      tasks.filter((task) => {
        if (hideCompletedTasks && task.timelineDone) return false;
        const start = new Date(task.startDate);
        const end = new Date(task.endDate);
        return (
          isWithinInterval(start, { start: windowStart, end: windowEnd }) ||
          isWithinInterval(end, { start: windowStart, end: windowEnd }) ||
          (isBefore(start, windowStart) && isAfter(end, windowEnd))
        );
      }),
    [tasks, windowStart, windowEnd, hideCompletedTasks]
  );

  const goPrev = () => {
    if (rangeMode === 'week') setAnchorDate((d) => subWeeks(d, 1));
    else if (rangeMode === 'month') setAnchorDate((d) => subMonths(d, 1));
  };

  const goNext = () => {
    if (rangeMode === 'week') setAnchorDate((d) => addWeeks(d, 1));
    else if (rangeMode === 'month') setAnchorDate((d) => addMonths(d, 1));
  };

  const goToday = () => setAnchorDate(startOfToday());

  const handleRangeMode = (_: React.MouseEvent<HTMLElement>, value: TimelineRangeMode | null) => {
    if (value) setRangeMode(value);
  };

  return (
    <Paper
      sx={{
        p: 1.5,
        width: 1,
        boxSizing: 'border-box',
        maxHeight: { xs: '78vh', md: '82vh' },
        overflowY: 'auto',
        overflowX: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 1 }}>
        <Typography variant="subtitle1" noWrap>
          {t('projects.timelineTitle')}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={hideCompletedTasks}
                onChange={(_, checked) => setHideCompletedTasks(checked)}
              />
            }
            label={<Typography variant="body2">{t('projects.timelineHideCompleted')}</Typography>}
            sx={{ mr: 0.5, ml: 0 }}
          />
          <ToggleButtonGroup
            size="small"
            value={rangeMode}
            exclusive
            onChange={handleRangeMode}
            aria-label={t('projects.timelineRangeMode')}
          >
            <ToggleButton value="week">{t('projects.timelineRangeWeek')}</ToggleButton>
            <ToggleButton value="month">{t('projects.timelineRangeMonth')}</ToggleButton>
            <ToggleButton value="full">{t('projects.timelineRangeFull')}</ToggleButton>
          </ToggleButtonGroup>

          {rangeMode !== 'full' && (
            <ButtonGroup size="small">
              <Button onClick={goPrev} aria-label="previous">
                <ChevronLeft fontSize="small" />
              </Button>
              <Button onClick={goToday}>{t('common.today')}</Button>
              <Button onClick={goNext} aria-label="next">
                <ChevronRight fontSize="small" />
              </Button>
            </ButtonGroup>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 260 }} noWrap>
            {format(windowStart, 'yyyy-MM-dd', { locale: dateLocale })} –{' '}
            {format(windowEnd, 'yyyy-MM-dd', { locale: dateLocale })}
          </Typography>
        </Box>
      </Box>

      {days.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 1 }}>
          {t('projects.timelineEmptyInRange')}
        </Alert>
      ) : (
        <Box sx={{ display: 'flex', width: 1, minWidth: 0, flexShrink: 0 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', width: 1, minWidth: 0 }}>
            {/* Month + day header: same grid as rows — label column + bar track (full card width) */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                width: 1,
                minWidth: 0,
                borderBottom: 1,
                borderColor: 'divider',
                flexShrink: 0,
                overflow: 'visible',
                zIndex: 1
              }}
            >
              <Box
                aria-hidden
                sx={[
                  LABEL_COL_SX,
                  {
                    borderRight: 1,
                    borderColor: 'divider'
                  }
                ]}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', width: 1, minWidth: 0 }}>
                {monthSegments.map((seg, idx) => {
                  const seed = seg.year * 12 + seg.monthIndex;
                  return (
                    <Box
                      key={`${seg.year}-${seg.monthIndex}-${idx}`}
                      sx={{
                        flex: seg.count,
                        minWidth: 0,
                        textAlign: 'center',
                        py: 0.5,
                        px: 0.25,
                        bgcolor: timelineMonthBackground(theme, seed, 'band'),
                        borderRight: idx < monthSegments.length - 1 ? 1 : 0,
                        borderColor: 'divider'
                      }}
                    >
                      <Tooltip title={seg.labelFull}>
                        <Typography
                          variant="caption"
                          component="div"
                          sx={{
                            fontSize: '0.72rem',
                            lineHeight: 1.25,
                            fontWeight: 600
                          }}
                          color="text.primary"
                          noWrap
                        >
                          {seg.label}
                        </Typography>
                      </Tooltip>
                    </Box>
                  );
                })}
              </Box>
              {/* Day numbers: week + month only — full range has too many columns; month bands + colors suffice */}
              {rangeMode !== 'full' && (
              <Box
                sx={{
                  display: 'flex',
                  width: 1,
                  minWidth: 0,
                  borderTop: 1,
                  borderColor: 'divider',
                  overflow: 'visible'
                }}
              >
                {days.map((d, dayIdx) => {
                  const seed = d.getFullYear() * 12 + d.getMonth();
                  const prev = dayIdx > 0 ? days[dayIdx - 1] : null;
                  const monthBoundary =
                    prev !== null &&
                    (prev.getMonth() !== d.getMonth() || prev.getFullYear() !== d.getFullYear());
                  const dayNum = format(d, 'd', { locale: dateLocale });
                  return (
                    <Box
                      key={dayKey(d)}
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        textAlign: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 1,
                        px: 0.25,
                        overflow: 'visible',
                        bgcolor: timelineMonthBackground(theme, seed, 'day'),
                        borderLeft: monthBoundary ? 1 : 0,
                        borderRight: 1,
                        borderColor: 'divider',
                        boxSizing: 'border-box'
                      }}
                    >
                      <Tooltip title={format(d, 'PPPP', { locale: dateLocale })}>
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{
                            m: 0,
                            p: 0,
                            display: 'inline-block',
                            fontWeight: 800,
                            fontVariantNumeric: 'tabular-nums',
                            lineHeight: 1,
                            fontSize: '1.05rem',
                            color: 'text.primary'
                          }}
                        >
                          {dayNum}
                        </Typography>
                      </Tooltip>
                    </Box>
                  );
                })}
              </Box>
              )}
              </Box>
            </Box>

            {/* Task rows — natural height; Paper scrolls when content exceeds maxHeight */}
            <Box sx={{ overflowX: 'hidden' }}>
              {visibleTasks.length === 0 ? (
                <Alert severity="info" sx={{ borderRadius: 1, m: 1 }}>
                  {t('projects.timelineEmptyInRange')}
                </Alert>
              ) : (
                visibleTasks.map((task) => {
                const start = new Date(task.startDate);
                const end = new Date(task.endDate);
                const clampedStart = isBefore(start, windowStart) ? windowStart : start;
                const clampedEnd = isAfter(end, windowEnd) ? windowEnd : end;

                const si = Math.max(0, dayKeys.indexOf(dayKey(clampedStart)));
                const ei = Math.max(si, dayKeys.indexOf(dayKey(clampedEnd)));
                const span = Math.max(ei - si + 1, 1);

                const leftPct = (si / dayCount) * 100;
                const widthPct = (span / dayCount) * 100;
                const barSx = getTaskPriorityBarSx(theme, task.priority);
                const projectBand = projectTimelineRowBand(theme, task.projectId);
                const doneGreen = alpha(
                  theme.palette.success.main,
                  theme.palette.mode === 'dark' ? 0.18 : 0.14
                );

                return (
                  <Box
                    key={`${task.projectId}-${task.id}`}
                    role={onTaskClick ? 'button' : undefined}
                    tabIndex={onTaskClick ? 0 : undefined}
                    onClick={() => onTaskClick?.(task.projectId, task.id)}
                    onKeyDown={(e) => {
                      if (!onTaskClick) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onTaskClick(task.projectId, task.id);
                      }
                    }}
                    sx={{
                      display: 'flex',
                      width: 1,
                      minWidth: 0,
                      alignItems: 'stretch',
                      minHeight: taskRowMinPx(task),
                      borderBottom: 1,
                      borderColor: 'divider',
                      bgcolor: projectBand,
                      ...(task.timelineDone && {
                        boxShadow: `inset 0 0 0 9999px ${doneGreen}`
                      }),
                      transition: 'filter 0.15s ease',
                      '&:hover': {
                        filter: theme.palette.mode === 'dark' ? 'brightness(1.06)' : 'brightness(0.96)'
                      },
                      cursor: onTaskClick ? 'pointer' : 'default',
                      '&:focus-visible': onTaskClick
                        ? { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 }
                        : undefined
                    }}
                  >
                    {/* Label */}
                    <Box
                      sx={[
                        LABEL_COL_SX,
                        {
                          pr: 0.75,
                          pl: 0.25,
                          py: 0.25,
                          alignSelf: 'center'
                        }
                      ]}
                    >
                      <Tooltip title={taskTimelineTooltip(task, start, end, dateLocale, t)}>
                        <Box sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                          {task.projectTitle && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              sx={{ fontSize: '0.7rem', lineHeight: 1.2, overflowWrap: 'anywhere' }}
                            >
                              {task.projectTitle}
                            </Typography>
                          )}
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '0.8rem',
                              lineHeight: 1.2,
                              whiteSpace: 'normal',
                              overflowWrap: 'anywhere'
                            }}
                          >
                            {task.title}
                          </Typography>
                          {task.assigneeNames.length > 0 && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                fontSize: '0.7rem',
                                lineHeight: 1.2,
                                whiteSpace: 'normal',
                                overflowWrap: 'anywhere'
                              }}
                            >
                              {task.assigneeNames.join(', ')}
                            </Typography>
                          )}
                        </Box>
                      </Tooltip>
                    </Box>

                    {/* Bar — priority color + truncated label (ellipsis); full text in tooltip + left column */}
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        position: 'relative',
                        minHeight: taskRowMinPx(task),
                        alignSelf: 'stretch'
                      }}
                    >
                      <Tooltip title={taskTimelineTooltip(task, start, end, dateLocale, t)}>
                        <Box
                          sx={{
                            position: 'absolute',
                            left: `${leftPct}%`,
                            width: `${widthPct}%`,
                            top: BAR_VERTICAL_INSET,
                            bottom: BAR_VERTICAL_INSET,
                            borderRadius: 1,
                            minWidth: 2,
                            display: 'flex',
                            alignItems: 'center',
                            px: 0.5,
                            overflow: 'hidden',
                            ...barSx
                          }}
                        >
                          <Typography
                            component="span"
                            variant="caption"
                            noWrap
                            sx={{
                              color: 'inherit',
                              fontSize: '0.68rem',
                              lineHeight: 1.2,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              minWidth: 0,
                              width: '100%',
                              fontWeight: 600
                            }}
                          >
                            {(() => {
                              const base = task.title;
                              if (!task.assigneeNames.length) return base;
                              return `${base} — ${task.assigneeNames.join(', ')}`;
                            })()}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </Box>
                  </Box>
                );
              })
              )}
            </Box>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default ProjectTimeline;
